import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  signal,
  computed,
  LOCALE_ID,
  ChangeDetectionStrategy,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { WarningNotification } from '../../../../utils/warning-notification/warning-notification';
import { ActionInterface } from '../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import { Tables } from '../../../../shared/ui/shared-tables/tables/tables';
import { PPReasonListFilter } from './p-p-reason-list-filter/p-p-reason-list-filter';
import {
  ApiErrorResponse,
  DELETE_REASON_ERROR_CODES,
} from '../../../../pages/admin-dashboard/partially-package-reasons/reasons-delete/reasons-delete.model';
import { PPReasonDeleteService } from '../../../../services/Partially-Package-Reasones-Delete/p-p-reason-delete';
// import {
//   PartiallyPackageReasonFilters,
//   ViewPartiallyPackageReason,
//   PPReasonListResponse,
//   ApiErrorResponse,
//   GetReasonsQueryParams,
//   PartiallyPackageReasonVTO,
// } from '../../../../pages/admin-dashboard/partially-package-reasons/reasons-list/p-p-reason-list.model';
import { PPReasonListService } from '../../../../services/Partially-Package-Reasones-List/p-p-reason-list';
import {
  GetReasonsQueryParams,
  PartiallyPackageReasonFilters,
  PartiallyPackageReasonVTO,
  PPReasonListResponse,
  ViewPartiallyPackageReason,
} from '../../../../pages/admin-dashboard/partially-package-reasons/reasons-list/p-p-reason-list.model';

const DEFAULT_FILTERS: PartiallyPackageReasonFilters = {
  reasonName: '',
  createdOnFrom: '',
  createdOnTo: '',
  lastModifiedOnFrom: '',
  lastModifiedOnTo: '',
  orderBy: 'CREATION_DATE',
  orderDir: 'DESC',
};

const NOTIFICATION_DURATION = 3000;
const DEFAULT_PAGE_SIZE = 25;
const MOCK_DELETE_DELAY = 300;

const ACTION_EDIT = 1;
const ACTION_DELETE = 2;

type DeleteDialogState = 'closed' | 'confirm' | 'in-use';
type WarningMessageType = 'edit' | 'delete';

@Component({
  selector: 'app-p-p-reason-list',
  imports: [ConfirmationDialog, Pagination, Tables, SuccessfulNotification, WarningNotification, PPReasonListFilter],
  templateUrl: './p-p-reason-list.html',
  styleUrl: './p-p-reason-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PPReasonList implements OnInit, OnDestroy {
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);
  private reasonsService = inject(PPReasonListService);

  private deleteService = inject(PPReasonDeleteService);

  private successTimeout?: ReturnType<typeof setTimeout>;
  private warningTimeout?: ReturnType<typeof setTimeout>;

  readonly isArabic = computed(() => this.locale.startsWith('ar'));

  currentPage = signal<number>(1);
  itemsPerPage = signal<number>(DEFAULT_PAGE_SIZE);
  totalItems = signal<number>(0);

  filters = signal<PartiallyPackageReasonFilters>({ ...DEFAULT_FILTERS });
  appliedFilters = signal<PartiallyPackageReasonFilters>({ ...DEFAULT_FILTERS });
  isFiltersApplied = signal<boolean>(false);

  allReasons = signal<ViewPartiallyPackageReason[]>([]);
  loading = signal<boolean>(false);
  errorGettingReasons = signal<string | null>(null);

  deleteDialogState = signal<DeleteDialogState>('closed');
  reasonToDelete = signal<ViewPartiallyPackageReason | null>(null);
  deleteLoading = signal<boolean>(false);
  deleteError = signal<string>('');

  showSuccessMessage = signal<boolean>(false);
  deletedReasonName = signal<string>('');
  showWarningMessage = signal<boolean>(false);
  warningMessageType = signal<WarningMessageType>('edit');

  readonly tableHeaders = computed(() => [
    { key: 'rowNumber', label: '#' },
    { key: 'title', label: this.isArabic() ? 'العنوان' : 'Title' },
    {
      key: 'typeStatus',
      label: this.isArabic() ? 'النوع' : 'Type',
      type: 'status',
    },
    {
      key: 'createdOnBy',
      label: this.isArabic() ? 'تاريخ الإنشاء / بواسطة' : 'Created On / By',
    },
    {
      key: 'lastModifiedOnBy',
      label: this.isArabic() ? 'آخر تعديل / بواسطة' : 'Last Modified On / By',
    },
    {
      key: 'actions',
      label: '',
      type: 'actions',
    },
  ]);

  readonly tableActions = computed<ActionInterface[]>(() => [
    { id: ACTION_EDIT, titleEn: 'Edit', titleAr: 'تعديل' } as ActionInterface,
    { id: ACTION_DELETE, titleEn: 'Delete', titleAr: 'حذف' } as ActionInterface,
  ]);

  readonly tableRows = computed<Record<string, any>[]>(() => {
    const startIndex = (this.currentPage() - 1) * this.itemsPerPage();

    return this.allReasons().map((reason, index) => ({
      id: reason.id,
      rowNumber: startIndex + index + 1,
      title: this.isArabic() ? reason.titleAr : reason.titleEn,
      typeStatus: {
        titleEn: reason.isEditable ? 'Custom' : 'System-defined',
        titleAr: reason.isEditable ? 'مخصص' : 'معرّف من النظام',
      },
      createdOn: reason.createdOn,
      createdBy: this.getCreatedByLabel(reason),
      lastModifiedOn: reason.lastModifiedOn,
      lastModifiedBy: reason.lastModifiedBy,
      isEditable: reason.isEditable,
      _original: reason,
    }));
  });

  readonly totalPages = computed<number>(() => Math.ceil(this.totalItems() / this.itemsPerPage()));

  readonly startItem = computed<number>(() => {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.itemsPerPage() + 1;
  });

  readonly endItem = computed<number>(() => {
    const end = this.currentPage() * this.itemsPerPage();
    return end > this.totalItems() ? this.totalItems() : end;
  });

  readonly getPageNumbers = computed<number[]>(() => {
    const pages: number[] = [];
    const total = this.totalPages();
    const current = this.currentPage();

    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push(-1);
      if (current > 2) pages.push(current - 1);
      if (current !== 1 && current !== total) pages.push(current);
      if (current < total - 1) pages.push(current + 1);
      if (current < total - 2) pages.push(-1);
      pages.push(total);
    }

    return pages;
  });

  readonly isDeleteDialogOpen = computed<boolean>(() => this.deleteDialogState() !== 'closed');

  readonly deleteDialogTitle = computed<string>(() => {
    const state = this.deleteDialogState();
    const ar = this.isArabic();

    switch (state) {
      case 'confirm':
        return ar ? 'حذف سبب التعبئة الجزئية' : 'Delete Partially Package Reason';
      case 'in-use':
        return ar ? 'لا يمكن حذف سبب التعبئة الجزئية' : 'Cannot Delete Partially Package Reason';
      default:
        return '';
    }
  });

  readonly deleteDialogDescription = computed<string>(() => {
    const state = this.deleteDialogState();
    const ar = this.isArabic();

    switch (state) {
      case 'confirm':
        return ar ? 'هل أنت متأكد من حذف' : 'Are you sure you want to delete';
      case 'in-use':
        return ar
          ? 'لا يمكن حذف سبب التعبئة الجزئية هذا لأنه معين حالياً لطلب أو أكثر. للحفاظ على سلامة البيانات، لا يمكن إزالة الأسباب المستخدمة من النظام.'
          : 'This partially package reason cannot be deleted because it is currently assigned to one or more orders. To maintain data integrity, reasons in use cannot be removed from the system.';
      default:
        return '';
    }
  });

  readonly deleteDialogItemName = computed<string>(() => {
    const state = this.deleteDialogState();
    const reason = this.reasonToDelete();

    if (!reason) return '';

    if (state === 'confirm' || state === 'in-use') {
      return this.isArabic() ? reason.titleAr : reason.titleEn;
    }

    return '';
  });

  readonly deleteDialogConfirmContent = computed<string>(() => {
    const state = this.deleteDialogState();
    const ar = this.isArabic();

    if (state === 'confirm') {
      return ar ? 'حذف' : 'Delete';
    }
    return ar ? 'إغلاق' : 'Close';
  });

  readonly deleteDialogCancelContent = computed<string>(() => {
    const state = this.deleteDialogState();
    if (state === 'confirm') {
      return this.isArabic() ? 'إلغاء' : 'Cancel';
    }
    return '';
  });

  readonly warningMessageText = computed<string>(() => {
    const type = this.warningMessageType();
    const ar = this.isArabic();

    if (type === 'edit') {
      return ar ? 'لا يمكن تعديل الأسباب المعرّفة من النظام' : 'System-defined reasons cannot be edited.';
    } else {
      return ar ? 'لا يمكن حذف الأسباب المعرّفة من النظام' : 'System-defined reasons cannot be deleted.';
    }
  });

  ngOnInit(): void {
    this.loadReasons();
  }

  ngOnDestroy(): void {
    if (this.successTimeout) clearTimeout(this.successTimeout);
    if (this.warningTimeout) clearTimeout(this.warningTimeout);
  }

  loadReasons(): void {
    this.loading.set(true);
    this.errorGettingReasons.set(null);

    const queryParams = this.buildQueryParams();

    this.reasonsService
      .getReasons(queryParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: PPReasonListResponse) => {
          const mappedReasons = this.mapReasonsForView(response.list ?? []);
          this.allReasons.set(mappedReasons);
          this.totalItems.set(response.total ?? 0);
          this.loading.set(false);
        },
        error: (err: ApiErrorResponse) => {
          this.loading.set(false);
          this.errorGettingReasons.set(
            this.isArabic()
              ? err?.messageAr || 'حدث خطأ أثناء تحميل الأسباب'
              : err?.messageEn || 'Error loading reasons',
          );
        },
      });
  }

  private buildQueryParams(): GetReasonsQueryParams {
    const filters = this.appliedFilters();

    return {
      quickSearch: filters.reasonName || undefined,
      pageNum: this.currentPage() - 1,
      pageSize: this.itemsPerPage(),
      orderBy: filters.orderBy,
      orderDir: filters.orderDir,
      createdOnFrom: filters.createdOnFrom || undefined,
      createdOnTo: filters.createdOnTo || undefined,
      lastModifiedOnFrom: filters.lastModifiedOnFrom || undefined,
      lastModifiedOnTo: filters.lastModifiedOnTo || undefined,
    };
  }

  private mapReasonsForView(reasons: PartiallyPackageReasonVTO[]): ViewPartiallyPackageReason[] {
    return reasons.map((r) => ({
      id: r.id,
      titleAr: r.titleAr,
      titleEn: r.titleEn,
      isEditable: r.isEditable,
      createdOn: r.createdOn,
      createdBy: r.createdBy?.fullName ?? '',
      lastModifiedOn: r.lastModifiedOn,
      lastModifiedBy: r.lastModifiedBy?.fullName ?? null,
    }));
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages() && page !== this.currentPage()) {
      this.currentPage.set(page);
      this.loadReasons();
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((p) => p + 1);
      this.loadReasons();
    }
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update((p) => p - 1);
      this.loadReasons();
    }
  }

  onPageSizeChange(newSize: number): void {
    this.itemsPerPage.set(Number(newSize));
    this.currentPage.set(1);
    this.loadReasons();
  }

  onApplyFilters(newFilters: PartiallyPackageReasonFilters): void {
    this.filters.set(newFilters);
    this.appliedFilters.set({ ...newFilters });
    this.currentPage.set(1);
    this.isFiltersApplied.set(this.checkIfFiltersApplied());
    this.loadReasons();
  }

  onResetFilters(): void {
    this.filters.set({ ...DEFAULT_FILTERS });
    this.appliedFilters.set({ ...DEFAULT_FILTERS });
    this.currentPage.set(1);
    this.isFiltersApplied.set(false);
    this.loadReasons();
  }

  onToggleSortOrder(): void {
    this.filters.update((f) => ({
      ...f,
      orderDir: f.orderDir === 'DESC' ? 'ASC' : 'DESC',
    }));
    this.appliedFilters.set({ ...this.filters() });
    this.loadReasons();
  }

  private checkIfFiltersApplied(): boolean {
    const f = this.appliedFilters();
    return !!(f.reasonName || f.createdOnFrom || f.createdOnTo || f.lastModifiedOnFrom || f.lastModifiedOnTo);
  }

  onActionTriggered(payload: { event: Event; actionId: number; entity: any }): void {
    const originalReason = payload.entity._original as ViewPartiallyPackageReason;

    if (payload.actionId === ACTION_EDIT) {
      if (originalReason.isEditable) {
        this.onEditReason(originalReason.id);
      } else {
        this.displayWarningMessage('edit');
      }
    } else if (payload.actionId === ACTION_DELETE) {
      if (originalReason.isEditable) {
        this.openDeleteDialog(originalReason);
      } else {
        this.displayWarningMessage('delete');
      }
    }
  }

  onEditReason(reasonId: number): void {
    this.router.navigate(['/admin/partially-package-reasons/edit-reason', reasonId]);
  }

  navigateToAddReason(): void {
    this.router.navigate(['/admin/partially-package-reasons/add-reason']);
  }

  openDeleteDialog(reason: ViewPartiallyPackageReason): void {
    this.reasonToDelete.set(reason);
    this.deleteError.set('');
    this.deleteDialogState.set('confirm');
  }

  closeDeleteDialog(): void {
    this.deleteDialogState.set('closed');
    this.reasonToDelete.set(null);
    this.deleteError.set('');
  }

  onDeleteDialogSubmit(): void {
    const state = this.deleteDialogState();

    if (state === 'confirm') {
      this.performDelete();
    } else {
      this.closeDeleteDialog();
    }
  }

  private performDelete(): void {
    const reason = this.reasonToDelete();
    if (!reason) return;

    this.deleteLoading.set(true);
    this.deleteError.set('');

    this.deleteService
      .deleteReason(reason.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.handleDeleteSuccess(reason);
        },
        error: (err: ApiErrorResponse) => {
          this.deleteLoading.set(false);
          this.handleDeleteError(err);
        },
      });
  }

  private handleDeleteError(err: ApiErrorResponse): void {
    if (err?.code === DELETE_REASON_ERROR_CODES.REASON_IN_USE) {
      this.deleteDialogState.set('in-use');
      return;
    }

    if (
      err?.code === DELETE_REASON_ERROR_CODES.REASON_NOT_EDITABLE ||
      err?.code === DELETE_REASON_ERROR_CODES.CANNOT_DELETE_SYSTEM_REASON
    ) {
      this.closeDeleteDialog();
      this.displayWarningMessage('delete');
      return;
    }

    if (err?.code === DELETE_REASON_ERROR_CODES.REASON_NOT_FOUND) {
      this.closeDeleteDialog();
      this.loadReasons();
      return;
    }

    this.deleteError.set(
      this.isArabic() ? err?.messageAr || 'حدث خطأ أثناء حذف السبب' : err?.messageEn || 'Error deleting reason',
    );
  }

  private handleDeleteSuccess(reason: ViewPartiallyPackageReason): void {
    const reasonName = this.isArabic() ? reason.titleAr : reason.titleEn;

    this.deleteLoading.set(false);
    this.closeDeleteDialog();

    this.deletedReasonName.set(reasonName);
    this.displaySuccessMessage();

    this.loadReasons();
  }

  private displaySuccessMessage(): void {
    this.showSuccessMessage.set(true);

    if (this.successTimeout) clearTimeout(this.successTimeout);
    this.successTimeout = setTimeout(() => {
      this.showSuccessMessage.set(false);
    }, NOTIFICATION_DURATION);
  }

  private displayWarningMessage(type: WarningMessageType): void {
    this.warningMessageType.set(type);
    this.showWarningMessage.set(true);

    if (this.warningTimeout) clearTimeout(this.warningTimeout);
    this.warningTimeout = setTimeout(() => {
      this.showWarningMessage.set(false);
    }, NOTIFICATION_DURATION);
  }

  private getCreatedByLabel(reason: ViewPartiallyPackageReason): string {
    if (!reason.isEditable) {
      return this.isArabic() ? 'النظام' : 'System';
    }
    return reason.createdBy;
  }
}
